import { BreadcrumbProps, IconName, Intent } from "@blueprintjs/core";
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useState
} from "react";

export type PlatformAction = {
    readonly icon: IconName;
    readonly label: string;
    readonly onClick?: () => void;
    readonly children?: ReadonlyArray<PlatformAction>;
    readonly intent?: Intent;
    readonly disabled?: boolean;
};

type PlatformShellContextType = {
    readonly actions: ReadonlyArray<PlatformAction>;
    readonly setActions: (actions: ReadonlyArray<PlatformAction>) => void;
    readonly breadcrumbs: ReadonlyArray<BreadcrumbProps>;
    readonly setBreadcrumbs: (
        breadcrumbs: ReadonlyArray<BreadcrumbProps>
    ) => void;
};

const PlatformShellContext = createContext<
    PlatformShellContextType | undefined
>(undefined);

export const PlatformShellProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const [actions, setActions] = useState<ReadonlyArray<PlatformAction>>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<
        ReadonlyArray<BreadcrumbProps>
    >([]);
    return (
        <PlatformShellContext.Provider
            value={{ actions, setActions, breadcrumbs, setBreadcrumbs }}
        >
            {children}
        </PlatformShellContext.Provider>
    );
};

const usePlatformShell = (): PlatformShellContextType => {
    const context = useContext(PlatformShellContext);
    if (context === undefined) {
        throw new Error(
            "usePlatformShell must be used within PlatformShellProvider"
        );
    }
    return context;
};

export const usePlatformActions = (): ReadonlyArray<PlatformAction> =>
    usePlatformShell().actions;

export const useSetPlatformActions = (): ((
    actions: ReadonlyArray<PlatformAction>
) => void) => usePlatformShell().setActions;

export const usePlatformBreadcrumbs = (): ReadonlyArray<BreadcrumbProps> =>
    usePlatformShell().breadcrumbs;

export const useSetPlatformBreadcrumbs = (): ((
    breadcrumbs: ReadonlyArray<BreadcrumbProps>
) => void) => usePlatformShell().setBreadcrumbs;
